import React from 'react';
import axios from 'axios';
import { Formik } from 'formik';
import { capitalize } from 'lodash';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import Typography from '~/components/core/Atomic/Typography';
import Text from '~/components/core/TextComponents/Text';
import useCurrencyFormatter from '~/components/CurrencyFormatterContext';
import GrossVatTooltip from '~/components/Finances/GrossVatTooltip';
import { usePaymentsConfiguration } from '~/components/hooks/usePaymentsConfiguration';
import LoadingIndicator from '~/components/LoadingIndicator';
import TextFieldFormik from '~/components/TextFieldFormik';
import useDataFetcher from '~/components/useDataFetcher';
import { reportAxiosError } from '~/Utils';

import groupByPayableType from './groupByPayableType';
import SingleGeneralRecoveryDialog from './SingleGeneralRecoveryDialog';
import SplitGeneralRecoveryDialog from './SplitGeneralRecoveryDialog';

interface GeneralRecoveryDialogProps {
  claimId: number;
  exposureIdOrNullIfGeneralExpenses: number | null;
  onClose: () => void;
  onUpdate: () => void | Promise<void>;
}

interface PayableWithReserveForGeneralRecovery {
  general_recoveries_sum: number | null;
  // NET (excl. VAT/GST) sibling of general_recoveries_sum, served alongside it. Optional so the
  // callers that build a payable shape of their own keep type-checking against the gross figure.
  net_general_recoveries_sum?: number | null;
}

interface GeneralRecoveryPayableWithReserveSumProps {
  payableWithReserve: PayableWithReserveForGeneralRecovery;
}

const GeneralRecoveryPayableWithReserveSum: React.FC<GeneralRecoveryPayableWithReserveSumProps> = ({
  payableWithReserve,
}) => {
  const { currencyFormatter } = useCurrencyFormatter();
  const { displayNetAmounts } = usePaymentsConfiguration();

  const completeColor = 'text-status-done';
  const recoverySum = payableWithReserve.general_recoveries_sum;

  if (!recoverySum || recoverySum <= 0) {
    return <></>;
  }

  // Recovery complete/pending with recovered syn
  // With the toggle on the line shows the recoveries net of the VAT/GST captured on them, with the
  // gross amount recorded in the hover hint, matching the paid sum rendered directly above it.
  const displayedSum = displayNetAmounts ? (payableWithReserve.net_general_recoveries_sum ?? recoverySum) : recoverySum;

  const sumLine = (
    <Typography variant="body2" className={completeColor}>
      Gen Rec: {currencyFormatter.format(displayedSum)}
    </Typography>
  );

  return displayNetAmounts ? (
    <GrossVatTooltip grossAmount={recoverySum} vatAmount={recoverySum - displayedSum}>
      {sumLine}
    </GrossVatTooltip>
  ) : (
    sumLine
  );
};

interface RecoveryAmount {
  amount: number;
  reference?: string;
  check_number?: string;
  note?: string;
  [key: string]: unknown;
}

interface RecoveryGroupAllocation {
  payable_type?: string;
  general_recovery_type?: string | null;
  general_recovery_type_desc?: string | null;
  amount: number;
}

interface RecoveryGroup {
  recovery_group_id: string;
  reference?: string;
  check_number?: string;
  note?: string;
  // summed by the server, so the dialog never adds up floats of its own
  total_amount: number;
  allocations: RecoveryGroupAllocation[];
  [key: string]: unknown;
}

interface CancelGeneralRecoveryDialogProps {
  claimId: number;
  exposureIdOrNullIfGeneralExpenses: number | null;
  payableType?: string;
  recoveryAmountId?: number;
  // when set, the whole split action is fetched and cancelled as one group
  recoveryGroupId?: string;
  onCancel: () => void;
  onUpdate: () => void | Promise<void>;
}

const CancelGeneralRecoveryDialog: React.FC<CancelGeneralRecoveryDialogProps> = ({
  claimId,
  exposureIdOrNullIfGeneralExpenses,
  payableType,
  recoveryAmountId,
  recoveryGroupId,
  onCancel,
  onUpdate,
}) => {
  const singleResourceUrl = exposureIdOrNullIfGeneralExpenses
    ? `/api/v1/claims/${claimId}/exposures/${exposureIdOrNullIfGeneralExpenses}/${payableType}/general_recoveries/${recoveryAmountId}`
    : `/api/v1/claims/${claimId}/general_expenses/general_recoveries/${recoveryAmountId}`;
  const baseResourceUrl = recoveryGroupId
    ? `/api/v1/claims/${claimId}/general_recovery_groups/${recoveryGroupId}`
    : singleResourceUrl;

  const { isLoading, isError, data: recovery } = useDataFetcher<RecoveryAmount | RecoveryGroup>(baseResourceUrl);
  const { classes } = useStyles();
  const { currencyFormatter } = useCurrencyFormatter();

  if (isLoading || isError || !recovery) {
    return (
      <CardDialog isDialog open onClose={onCancel} title="Cancel Recovery">
        <LoadingIndicator isError={isError || (!isLoading && !recovery)} />
      </CardDialog>
    );
  }

  const allocations = recoveryGroupId ? ((recovery as RecoveryGroup).allocations ?? []) : [];
  const totalAmount = recoveryGroupId ? (recovery as RecoveryGroup).total_amount : (recovery as RecoveryAmount).amount;

  return (
    <Formik
      initialValues={{ cancellation_note: '' }}
      validationSchema={Yup.object().shape({ cancellation_note: Yup.string().required('Required') })}
      onSubmit={async (values, formikProps) => {
        try {
          await axios.delete(baseResourceUrl, { data: { ...values } });
          await onUpdate();
          formikProps.resetForm();
        } catch (error) {
          reportAxiosError(error);
          formikProps.setSubmitting(false);
        }
      }}
      enableReinitialize
    >
      {({ isSubmitting, handleSubmit }) => {
        return (
          <CardDialog isDialog onClose={onCancel} title="Cancel Recovery" preventClose={isSubmitting}>
            <div className={classes.textField}>
              <Text weight={Text.WEIGHTS.REGULAR}>Recovery of amount: {currencyFormatter.format(totalAmount)}</Text>
              {allocations.length > 0 && (
                <div className="mt-4 flex flex-col gap-4 pl-12">
                  {groupByPayableType(allocations).map((group) => (
                    <div key={group.payableType} className="flex flex-col gap-2">
                      <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.SEMI_BOLD}>
                        {capitalize(group.payableType)}
                      </Text>
                      {group.lines.map((allocation, index) => {
                        const label = allocation.general_recovery_type_desc || allocation.general_recovery_type;
                        const amount = currencyFormatter.format(allocation.amount);
                        return (
                          <Text key={index} variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR}>
                            {label ? `${label}: ${amount}` : amount}
                          </Text>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
              {recovery.reference && <Text weight={Text.WEIGHTS.REGULAR}>Reference: {recovery.reference}</Text>}
              {recovery.check_number && (
                <Text weight={Text.WEIGHTS.REGULAR}>Check Number: {recovery.check_number}</Text>
              )}
              {recovery.note && <Text weight={Text.WEIGHTS.REGULAR}>Note: {recovery.note}</Text>}
              {allocations.length > 1 && (
                <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR}>
                  Cancelling this recovery cancels all of its allocations.
                </Text>
              )}
            </div>
            <TextFieldFormik id="cancellation_note" label="Reason" fullWidth className={classes.textField} />
            <div className={classes.buttonsContainer}>
              <Button variant="contained" color="primary" disabled={isSubmitting} onClick={() => handleSubmit()}>
                Cancel Recovery
              </Button>
            </div>
          </CardDialog>
        );
      }}
    </Formik>
  );
};

// Creation is the only surface the split recovery setting gates: with it off, recoveries are added
// exactly as before split recoveries existed (single amount, legacy general_recoveries endpoints).
const GeneralRecoveryDialog: React.FC<GeneralRecoveryDialogProps> = (props) => {
  const { isSplitGeneralRecoveryEnabled } = usePaymentsConfiguration();
  return isSplitGeneralRecoveryEnabled ? (
    <SplitGeneralRecoveryDialog {...props} />
  ) : (
    <SingleGeneralRecoveryDialog {...props} />
  );
};

export default GeneralRecoveryDialog;
export { CancelGeneralRecoveryDialog, GeneralRecoveryPayableWithReserveSum };
